import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Modal, Form, Input, Select, Space, message, Row, Col, Segmented, Switch, InputNumber } from "antd";
import { PlusOutlined, FileTextOutlined } from "@ant-design/icons";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Option } = Select;

const statusOptions = ["Submitted", "Under Review", "Approved", "Rejected", "On Hold", "Disbursed"];
const productOptions = ["lending", "indigent"];
const channelOptions = ["SELF", "FRONT_DESK", "AGENT"];

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: [], productType: [] });
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("applications.viewMode");
    return saved === "table" || saved === "cards" ? saved : "cards";
  });
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("applications.isDark");
    return saved === null ? true : saved === "true";
  });
  const useDarkStyles = true;

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(API_CONFIG.applicationsListUrl, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load applications");
      setApplications(data.applications || []);
    } catch (err) {
      message.error(err.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    localStorage.setItem("applications.viewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("applications.isDark", isDark);
  }, [isDark]);

  const handleSave = async (values) => {
    try {
      const res = await apiFetch(API_CONFIG.applicationsSaveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to save application");
      message.success("Application saved");
      setOpen(false);
      fetchApplications();
    } catch (err) {
      message.error(err.message || "Failed to save application");
    }
  };

  const updateStatus = async (application, status) => {
    await apiFetch(API_CONFIG.applicationsStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: application.id, status }),
    });
    fetchApplications();
  };

  const openComment = (application) => {
    setSelectedApp(application);
    commentForm.resetFields();
    setCommentOpen(true);
  };

  const submitComment = async (values) => {
    await apiFetch(API_CONFIG.applicationsCommentUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: selectedApp.id, text: values.comment }),
    });
    setCommentOpen(false);
    fetchApplications();
  };

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const next = new Set(prev[key] || []);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: Array.from(next) };
    });
  };

  const clearFilters = () => setFilters({ status: [], productType: [] });

  const filteredApps = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return applications.filter((app) => {
      const matchesSearch =
        !term ||
        [app.id, app.applicantName, app.identityNumber, app.purpose]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term));
      const matchesStatus = filters.status.length === 0 || filters.status.includes(app.status);
      const matchesProduct = filters.productType.length === 0 || filters.productType.includes(app.productType);
      return matchesSearch && matchesStatus && matchesProduct;
    });
  }, [applications, searchTerm, filters]);

  const stats = useMemo(() => {
    const total = applications.length;
    const byStatus = statusOptions.reduce((acc, s) => {
      acc[s] = applications.filter((a) => a.status === s).length;
      return acc;
    }, {});
    return { total, ...byStatus };
  }, [applications]);

  const columns = [
    { title: "App ID", dataIndex: "id", key: "id", width: 130 },
    { title: "Applicant", dataIndex: "applicantName", key: "applicantName" },
    { title: "ID Number", dataIndex: "identityNumber", key: "identityNumber", width: 150 },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (v) => `R ${Number(v || 0).toLocaleString()}` },
    { title: "Product", dataIndex: "productType", key: "productType", width: 120 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (_v, app) => (
        <Select value={app.status} onChange={(v) => updateStatus(app, v)} size="small">
          {statusOptions.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Select>
      ),
    },
    { title: "Channel", dataIndex: "channel", key: "channel", width: 120 },
    { title: "Created", dataIndex: "createdAt", key: "createdAt", render: (v) => v?.slice?.(0, 10) },
    {
      title: "Actions",
      key: "actions",
      render: (_v, app) => (
        <Space>
          <Button className="luxe-btn" size="small" onClick={() => openComment(app)}>Comment</Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
      style={{
        background: isDark ? "#000" : "#fff",
        minHeight: "100vh",
        marginLeft: "var(--sidenav-width, 88px)",
        padding: "0 20px 0 12px",
        position: "relative",
        zIndex: 1002,
      }}
    >
      <Row gutter={0}>
        <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24 luxe-dark-bg"
            style={{ margin: 0, position: "relative", zIndex: 1003 }}
            title={<span className="luxe-card-title">Loan Applications</span>}
            extra={
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Cards", value: "cards" },
                      { label: "Table", value: "table" },
                    ]}
                    value={viewMode}
                    onChange={(v) => setViewMode(v)}
                  />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 4 }}>
                  <span style={{ color: "#fff8dc", fontWeight: 600 }}>Dark</span>
                  <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                </div>
              </div>
            }
            bodyStyle={{ background: "#000", borderRadius: "18px" }}
          >
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="kpi-card">
                <div className="kpi-title">Total Applications</div>
                <div className="kpi-value">{stats.total}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Submitted</div>
                <div className="kpi-value">{stats["Submitted"] || 0}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Under Review</div>
                <div className="kpi-value">{stats["Under Review"] || 0}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Approved</div>
                <div className="kpi-value">{stats["Approved"] || 0}</div>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <Button icon={<PlusOutlined />} className="luxe-btn" onClick={() => setOpen(true)}>New Application</Button>
              <Input.Search
                placeholder="Search by ID, name, ID number, purpose"
                className="luxe-search"
                enterButton={<Button className="luxe-search-btn">Search</Button>}
                onSearch={(v) => setSearchTerm(v)}
                allowClear
                style={{ width: 320 }}
              />
            </div>

            <div className="filter-chips">
              {statusOptions.map((status) => (
                <span
                  key={status}
                  className={`chip-filter ${filters.status.includes(status) ? "active" : ""}`}
                  onClick={() => toggleFilter("status", status)}
                >
                  {status}
                </span>
              ))}
              {productOptions.map((p) => (
                <span
                  key={p}
                  className={`chip-filter ${filters.productType.includes(p) ? "active" : ""}`}
                  onClick={() => toggleFilter("productType", p)}
                >
                  {p}
                </span>
              ))}
              {(filters.status.length > 0 || filters.productType.length > 0) && (
                <span className="chip-filter clear" onClick={clearFilters}>Clear</span>
              )}
            </div>

            {viewMode === "cards" ? (
              <div className="indigent-grid" style={{ marginTop: 16 }}>
                {filteredApps.map((app) => (
                  <div key={app.id} className="indigent-card">
                    <div className="indigent-head">
                      <div className="indigent-avatar">{String(app.applicantName || "A")[0]}</div>
                      <div>
                        <div className="indigent-name">{app.applicantName || "Unknown"}</div>
                        <div className="indigent-meta">{app.id} · {app.identityNumber || "No ID"}</div>
                      </div>
                    </div>
                    <div className="indigent-line">
                      <span>Amount: R {Number(app.amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="indigent-line">
                      <span>Product: {app.productType || "lending"}</span>
                    </div>
                    <div className="indigent-tags">
                      <span className="chip dark">{app.status}</span>
                      <span className="chip dark">Channel: {app.channel || "SELF"}</span>
                    </div>
                    <div className="indigent-actions">
                      <Button className="luxe-btn" icon={<FileTextOutlined />} onClick={() => openComment(app)}>Comment</Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table
                rowKey="id"
                loading={loading}
                dataSource={filteredApps}
                columns={columns}
                className="luxe-table"
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="New Application"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="applicantName" label="Applicant Name" rules={[{ required: true, message: "Enter name" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="identityNumber" label="ID Number" rules={[{ required: true, message: "Enter ID" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: "Enter amount" }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="productType" label="Product" initialValue="lending">
            <Select>
              {productOptions.map((p) => (
                <Option key={p} value={p}>{p}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="purpose" label="Purpose">
            <Input />
          </Form.Item>
          <Form.Item name="channel" label="Channel" initialValue="FRONT_DESK">
            <Select>
              {channelOptions.map((c) => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="Submitted">
            <Select>
              {statusOptions.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Comment on ${selectedApp?.id || ""}`}
        open={commentOpen}
        onCancel={() => setCommentOpen(false)}
        onOk={() => commentForm.submit()}
        okText="Save"
      >
        <Form form={commentForm} layout="vertical" onFinish={submitComment}>
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: "Enter comment" }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
